import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Form, Input, Select} from "antd";
import {EditableContext} from "./editableTable";
import ValueCell from "./valueCell";
import {FHIR_TABLE_CELL_HEIGHT} from "../../consts";

const { Option } = Select;

export const EditableCell = ({
                          title,
                          editable,
                          children,
                          dataIndex,
                          record,
                          handleSave,
                          width,
                          suggestions,
                          moveToScrollMode,
                          ...restProps
                      }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(children);
    const inputRef = useRef(null);
    const form = useContext(EditableContext);


    useEffect(() => {
        if (editing) {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    }, [editing]);

    const toggleEdit = () => {
        moveToScrollMode(!editing);
        setEditing(!editing);
        form.setFieldsValue({
            [dataIndex]: record[dataIndex],
        });
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            toggleEdit();
            handleSave({ ...record, ...values });
        } catch (errInfo) {
            console.log('Save failed:', errInfo);
        }
    };

    let childNode = children;

    const getRules = (dataIndex) => {
        if (dataIndex === "path" || dataIndex === "value") {
            return [
                {
                    required: true,
                    validateTrigger: 'onBlur',
                    message: `Please ensure all quotes are opened and closed. ""`,
                    validator: (_, value) => {
                        let numQuotes = (value.match(/"/g) || []).length;
                        return numQuotes % 2 === 0 ? Promise.resolve() : Promise.reject(new Error('Please check that all quotes are opened and closed.'))
                    }
                },
                {
                    required: true,
                    validateTrigger: 'onBlur',
                    message: `Please ensure all parentheses are opened and closed. ()`,
                    validator: (_, value) => {
                        let openParen = (value.match(/\(/g) || []).length;
                        let closeParen = (value.match(/\)/g) || []).length;
                        return openParen === closeParen ? Promise.resolve() : Promise.reject(new Error('Please check that all parentheses are opened and closed.'))
                    }
                }
            ]
        } else {
            return []
        }
    }

    function handleDrivingChange(value) {
        let tmp = {driving: value}
        handleSave({ ...record, ...tmp })
    }

    function handleValueChange(v) {
        let tmp = {value: v}
        handleSave({ ...record, ...tmp })
        toggleEdit()
    }

    /**
     * Whenever a value cell changes, update the state to allow auto completion
     * to run as well. Cases to consider:
     * 1.) For + or -,

    function handleValueChange(value) {
        setValue(value.target.value);
    }*/

    /***
     * For most cells, return a string value which may be edited in an <Input />
     *
     * For the driving column, return a dropdown which will prepopulate with options based on the path depth.
     * This is to ensure that a user never accidentally sets a driving depth too high and unintentionally removes
     * the base resource from the final output.
     * @returns {JSX.Element|*}
     */
    const getCellContent = () => {
        if (dataIndex === 'driving') {
            let pathDepth = record.path.split('.')
            const parentOptions = [...Array(pathDepth.length - 1)].map((_, i) => {
                let value = (i + 1) === 1 ? "parent" : `parent:${i+1}`;
                return <Option data-cy={`cell-${record.id}-driving-option-${value}`} value={value} key={Math.random()}>
                    {value}
                </Option>
            })

            return <Select defaultValue={children}
                           disabled={!editable}
                           data-cy={`cell-${record.id}-driving-select`}
                           style={{ width: 120 }}
                           onChange={handleDrivingChange}>
                <Option value={null}/>
                <Option value="self">self</Option>
                {parentOptions}
            </Select>
        } else {
            return <div style={{
                width: width,
                maxHeight: FHIR_TABLE_CELL_HEIGHT,
                overflowY: "hidden",
                overflowWrap: "break-word",
                lineHeight: "1.2em"
            }}>{childNode}</div>
        }
    }
    let inputContent;
    inputContent = useMemo(() => {
        if (dataIndex === 'path') {
            return <Input style={{width: width}}
                ref={inputRef}
                onPressEnter={save}
                onBlur={save}
            />
        }
        if (dataIndex === 'value') {
            const ForwardedRefValueCell = React.forwardRef((props, ref) => (
                <ValueCell
                    autoCompleteMode={children === ''}
                    width={width}
                    forwardedRef={ref}
                    suggestions={suggestions}
                    value={value}
                    handleValueChange={handleValueChange}
                    onPressEnter={save}
                    onBlur={save}
                    save={save}
                    setValue={setValue}
                    record={record}
                    dataIndex={dataIndex}
                />
            ));
            return <ForwardedRefValueCell ref={inputRef} />;
        }
    })


    if (editable && (dataIndex === 'path' || dataIndex === 'value')) {
        childNode = editing ? (
            <Form.Item
                style={{margin: 0,}}
                name={dataIndex}
                data-cy={`cell-${record.id}-${dataIndex}-input`}
                rules={getRules(dataIndex)}>
                    { inputContent }
            </Form.Item>
        ) : (
            <div
                className="editable-cell-value-wrap"
                data-cy={`cell-${record.id}-${dataIndex}`}
                style={{
                    width: width,
                    maxHeight: FHIR_TABLE_CELL_HEIGHT,
                    overflowY: "hidden",
                    overflowWrap: "break-word",
                    lineHeight: "1.2em"
                }}
                onClick={toggleEdit}>
                { children }
            </div>
        );
    }

    return <td
        style={{
        borderBottom: "1px solid #f0f0f0",
        verticalAlign: "middle",
        height: FHIR_TABLE_CELL_HEIGHT,
    }}
               {...restProps}>
        {getCellContent()}
    </td>;
};